import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  SubscriptionInfo,
  VerticalRow,
} from "components/SubscriptionOverviewComponents";
import { FRACTIONAL_PAYROLL_ADMINISTRATOR_MANAGE } from "constants/addons";
import { SOMETHING_WENT_WRONG } from "constants/apiCallError";
import { MM_SPACE_YYYY } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { capitalize } from "utils/capitalize";
import { formatDate } from "utils/formatDate";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCancelSubscriptionMutation } from "store/apis/subscriptions";
import { resetAllAddonDataPayload, setAddonType } from "store/slices/addons";
import { RootState } from "store/store";

export const FractionalPayrollAdministratorManage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { addonType, addonData } = useSelector(
    (state: RootState) => state.addonsSlice
  );
  const cancelationModal = useModal();
  const isOpen = addonType === FRACTIONAL_PAYROLL_ADMINISTRATOR_MANAGE;
  const [cancelSubscription, { isLoading }] = useCancelSubscriptionMutation();

  const currentGroupSubscription = addonData?.group_subscription?.[0];

  const handleClose = () => {
    dispatch(setAddonType(""));
    dispatch(resetAllAddonDataPayload());
  };

  const handleContactUs = () => {
    handleClose();
    history.push("/chat");
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription({
        groupId,
        entityId,
        subscriptionId: currentGroupSubscription?.group_subscription_id,
      }).unwrap();
      successToast({ message: "Subscription cancelled successfully" });
      cancelationModal.close();
      handleClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      alertToast({ message: error?.message || SOMETHING_WENT_WRONG });
      cancelationModal.close();
      handleClose();
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={handleClose}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{addonData?.subscription_name}</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-grid t-grid-cols-5 t-items-center t-gap-y-6">
              <SubscriptionInfo
                label="Started from"
                value={
                  currentGroupSubscription?.started_on
                    ? dayjs(currentGroupSubscription.started_on).format(
                        MM_SPACE_YYYY
                      )
                    : "-"
                }
              />
              <VerticalRow />
              {addonData?.billing_cycle && (
                <>
                  <SubscriptionInfo
                    label="Frequency"
                    value={capitalize(addonData.billing_cycle)}
                  />
                  <VerticalRow />
                </>
              )}
              <SubscriptionInfo
                label="Next Billing Date"
                value={
                  currentGroupSubscription?.ends_on
                    ? formatDate(currentGroupSubscription.ends_on)
                    : "-"
                }
              />
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={handleContactUs} customType="ghost">
              Contact us
            </Button>
            <Button onClick={cancelationModal.open}>Cancel subscription</Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={cancelationModal.isOpen}
        onOpenChange={cancelationModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Cancel subscription</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-text-body-sm">
              Are you sure you want to cancel the subscription?
            </div>
          </Modal.Body>
          <Modal.Footer className="t-flex t-justify-end t-gap-3">
            <Button type="button" onClick={cancelationModal.close}>
              Cancel
            </Button>
            <Button
              type="button"
              customType="danger"
              onClick={handleCancelSubscription}
              isLoading={isLoading}
            >
              Cancel subscription
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
